import { NotFoundError } from "../../types";
import { ValidationError } from "../errors";
import { isEmail, normaliseEmail } from "../logic/codes";
import { toBase64Url } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import {
  PLAN_IDS,
  type Allowances,
  type ProvisionInput,
  type ProvisionResult,
  type StaffApi,
} from "../types";
import type { Ctx } from "./context";
import { createInvitation } from "./organisation";
import type { StoredOrganisation } from "./state";
import { invitationView, orgRequestView, staffOrgView } from "./views";

/**
 * Staff console v0 (FR-ADMIN-051, 052): provisions organisations with counts-only allowances and invites the first
 * Owner. Live: a separate host behind staff sign-in; provisioning creates the WorkOS organisation, our record and
 * the Owner invitation (WorkOS sends the email). The schema has a plan (D-034) but no price, currency, invoice or payment field.
 */

const PREFIX = /^[A-Z]{1,6}$/;
const NAME_MAX = 80;
const WORKOS_ID_BYTES = 12;

function checkAllowances(allowances: Allowances): Allowances {
  const whole = (value: number, field: string, min: number) => {
    if (!Number.isInteger(value) || value < min)
      throw new ValidationError(field, `Enter a whole number of at least ${String(min)}.`);
    return value;
  };
  return {
    seats: whole(allowances.seats, "seats", 1),
    guests: whole(allowances.guests, "guests", 0),
    storageGb: whole(allowances.storageGb, "storageGb", 1),
  };
}

function checkDates(from: string, until: string): void {
  if (Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(until)))
    throw new ValidationError("accessUntil", "Enter both access dates.");
  if (until < from)
    throw new ValidationError("accessUntil", "The end date must be after the start date.");
}

export function createStaffApi(ctx: Ctx): StaffApi {
  const provision = async (input: ProvisionInput): Promise<ProvisionResult> => {
    const name = input.organisationName.trim();
    if (name === "" || name.length > NAME_MAX)
      throw new ValidationError("organisationName", "Enter the organisation's name.");
    if (!PREFIX.test(input.projectPrefix))
      throw new ValidationError("projectPrefix", "Use 1 to 6 capital letters, like NL.");
    const ownerEmail = normaliseEmail(input.ownerEmail);
    if (!isEmail(ownerEmail)) throw new ValidationError("ownerEmail", "Enter the Owner's email.");
    for (const [field, value] of [
      ["country", input.country],
      ["state", input.state],
      ["city", input.city],
    ] as const) {
      if (value.trim() === "") throw new ValidationError(field, "This can't be empty.");
    }
    checkDates(input.accessFrom, input.accessUntil);
    const org: StoredOrganisation = {
      id: `org-${ctx.token().slice(0, 10).toLowerCase()}`,
      // Live: the id WorkOS returns from POST /organizations.
      workosId: `org_01${toBase64Url(
        ctx.options.random.getRandomValues(new Uint8Array(WORKOS_ID_BYTES)),
      )
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "0")}`,
      name,
      projectPrefix: input.projectPrefix,
      country: input.country.trim(),
      state: input.state.trim(),
      city: input.city.trim(),
      region: IDENTITY_POLICY.storageRegion.id,
      status: "active",
      allowances: checkAllowances(input.allowances),
      storageUsedGb: 0,
      accessFrom: input.accessFrom,
      accessUntil: input.accessUntil,
      plan: input.plan,
      setupComplete: false,
      twoStepRequired: false,
      createdAt: ctx.now(),
    };
    ctx.state.organisations.push(org);
    const invitation = await createInvitation(ctx, {
      organisationId: org.id,
      email: ownerEmail,
      role: "owner",
      invitedBy: "Orthos",
    });
    return { organisation: staffOrgView(ctx, org), invitation: invitationView(ctx, invitation) };
  };

  return {
    async listAccessRequests() {
      await ctx.wait();
      return [...ctx.state.orgRequests]
        .sort(
          (a, b) =>
            Number(b.status === "pending") - Number(a.status === "pending") ||
            b.createdAt - a.createdAt,
        )
        .map(orgRequestView);
    },

    async approveAccessRequest(requestId, input) {
      await ctx.wait();
      const request = ctx.state.orgRequests.find((r) => r.id === requestId);
      if (request === undefined) throw new NotFoundError("Request");
      if (request.status !== "pending")
        throw new ValidationError("request", "This request was already answered.");
      const result = await provision(input);
      request.status = "approved";
      request.decidedAt = ctx.now();
      request.organisationId = result.organisation.id;
      ctx.commit();
      return result;
    },

    async declineAccessRequest(requestId, message) {
      await ctx.wait();
      const request = ctx.state.orgRequests.find((r) => r.id === requestId);
      if (request === undefined) throw new NotFoundError("Request");
      request.status = "declined";
      request.decidedAt = ctx.now();
      request.decisionMessage = message.trim();
      ctx.commit();
    },

    async listOrganisations() {
      await ctx.wait();
      return [...ctx.state.organisations]
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((org) => staffOrgView(ctx, org));
    },

    async createOrganisation(input) {
      await ctx.wait();
      const result = await provision(input);
      ctx.commit();
      return result;
    },

    async updateAllowances(organisationId, allowances, accessUntil) {
      await ctx.wait();
      const org = ctx.state.organisations.find((o) => o.id === organisationId);
      if (org === undefined) throw new NotFoundError("Organisation");
      checkDates(org.accessFrom, accessUntil);
      // Lowering below use never removes anyone; new invitations are blocked instead (FR-ADMIN-056).
      org.allowances = checkAllowances(allowances);
      org.accessUntil = accessUntil;
      ctx.commit();
      return staffOrgView(ctx, org);
    },

    async setPlan(organisationId, plan) {
      await ctx.wait();
      const org = ctx.state.organisations.find((o) => o.id === organisationId);
      if (org === undefined) throw new NotFoundError("Organisation");
      if (plan !== null && !PLAN_IDS.includes(plan))
        throw new ValidationError("plan", "Choose Studio or not activated.");
      org.plan = plan;
      ctx.commit();
      return staffOrgView(ctx, org);
    },
  };
}
